'use no memo';

/* eslint-disable react/jsx-no-constructed-context-values, complexity */
import { isCateringOrderFlow, isDineInOrderFlow } from '@local/utils';
import {
  getCountryCode,
  getCurrency,
  MembershipPlan,
  Restaurant,
} from '@local/api';

import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import { useLocation } from 'react-router';

import {
  isClosed,
  isDeliveryAheadAvailable,
  isDeliveryAvailableNow,
  isPickupAheadAvailable,
  isPickupAvailableNow,
} from '@local/data-utils/restaurant';

import { MAX_ORDER_CLOSURE_DESCRIPTION } from 'helpers/constants';
import { MODAL_TYPE } from 'helpers/modals';
import { FULFILLMENT_METHODS } from 'helpers/order';
import {
  getIsTableRequired,
  getIsTippingEnabled,
  getNextAvailableTime,
  getRestaurantHasDelivery,
  getFulfillmentFromUrlQuery,
  getIsFulfillmentAvailableForWhen,
  getRestaurantMembershipEnabled,
  getRestaurant,
} from 'helpers/restaurantApi';
import { getRestaurantIdFromUrl, isOnLocations, isOnPage } from 'helpers/url';

import useAssessFeatureFlag, { FLAG_NAME } from 'hooks/useAssessFeatureFlag';

import { FulfillMethod } from 'types/order';
import { OrderContext } from './OrderContext';

interface RestaurantProviderProps {
  children: React.ReactNode;
}

export type RestaurantContextProps = {
  fulfillmentEta: { pickup?: string; delivery?: string };
  handleFetchRestaurant: (id: string) => Promise<Restaurant | undefined>;
  isRestaurantLoading: boolean;
  isTableRequired: boolean;
  // $TSFIXME: wrong return type
  getIsFulfillmentAvailableForWhen: (
    fulfillment: FulfillMethod,
    when: string
  ) => void;
  // $TSFIXME: wrong return type
  getIsTippingEnabled: (fulfillment: FulfillMethod) => void;
  getNextAvailableTime: (
    fulfillment?: string
  ) => ReturnType<typeof getNextAvailableTime>;
  // $TSFIXME: wrong return type
  resetRestaurantState: () => void;
  restaurant: Restaurant | undefined;
  restaurantMembership: {
    plans: MembershipPlan[];
    enabled: boolean;
  };
  setTriggerHandleDeliveryOrderFromURLParam: (trigger: boolean) => void;
  setTriggerModal: (trigger: {
    modalType?: string;
    modalProps?: object;
  }) => void;
  triggerHandleDeliveryOrderFromURLParam: boolean;
  triggerModal: { modalType?: string; modalProps?: object };
};

export const DEFAULT_RESTAURANT_CONTEXT_VALUE: RestaurantContextProps = {
  fulfillmentEta: { pickup: undefined, delivery: undefined },
  handleFetchRestaurant: () => {
    throw new Error('not implemented');
  },
  isRestaurantLoading: false,
  isTableRequired: false,
  getIsFulfillmentAvailableForWhen: () => {},
  getIsTippingEnabled: () => {},
  getNextAvailableTime: (): never => {
    throw new Error('not implemented');
  },
  resetRestaurantState: () => {},
  restaurant: undefined,
  restaurantMembership: {
    plans: [],
    enabled: false,
  },
  setTriggerHandleDeliveryOrderFromURLParam: () => {},
  setTriggerModal: () => {},
  triggerHandleDeliveryOrderFromURLParam: false,
  triggerModal: {},
};

const RestaurantContext = createContext(DEFAULT_RESTAURANT_CONTEXT_VALUE);

function RestaurantProvider({ children }: RestaurantProviderProps) {
  /* @todo move this down inside the `/order/:restaurantId/...` Route
   * so we can use `useParams` and be reactive on that */
  const path = useLocation().pathname;
  const restaurantId = getRestaurantIdFromUrl(path);

  const { startOrder, order } = useContext(OrderContext);
  const isCateringEnabled = useAssessFeatureFlag(FLAG_NAME.flagCatering);

  const [isRestaurantLoading, setIsRestaurantLoading] = useState(false);
  const [restaurantState, setRestaurantState] = useState<Restaurant>();
  const [
    triggerHandleDeliveryOrderFromURLParam,
    setTriggerHandleDeliveryOrderFromURLParam,
  ] = useState(false);
  const [triggerModal, setTriggerModal] = useState({});
  const isOnCateringUrl = isCateringOrderFlow();

  if (
    restaurantState &&
    restaurantId &&
    (restaurantId !== restaurantState.id || isOnLocations())
  ) {
    // immediately invalidate restaurant state when the URL ID changes
    // this prevents down-stream effects from firing with the wrong ID
    setRestaurantState(undefined);
  }

  function handleFetchRestaurantSuccess(restaurant: Restaurant | undefined) {
    if (!restaurant) {
      return;
    }

    // check if catering is available when on catering URL
    if (isOnCateringUrl && !restaurant.has_catering) {
      return;
    }

    const isDeliveryOnly = restaurant.delivery_only;
    const hasDineInUrl = isDineInOrderFlow();
    const isDineInOrder =
      order && order.fulfill_method === FULFILLMENT_METHODS.dineIn;
    const isWrongDineInType =
      (isDineInOrder && !hasDineInUrl) || (!isDineInOrder && hasDineInUrl);
    const isWrongCateringType =
      (isOnCateringUrl && !order?.is_catering) ||
      (!isOnCateringUrl && order?.is_catering);
    const isWrongOrderType = isWrongDineInType || isWrongCateringType;
    const { fulfillMethod, nextAvailableTime } =
      getNextAvailableTime(restaurant);
    const hasDelivery = getRestaurantHasDelivery(restaurant);

    // Ignore any query params for dine-in orders
    // dine-in orders are indicated by url *path* as opposed to a query param
    const urlQueryFulfillMethod = !hasDineInUrl
      ? getFulfillmentFromUrlQuery(hasDelivery)
      : '';
    const defaultFulfillMethod = urlQueryFulfillMethod || fulfillMethod;
    const isClosedNow =
      !isPickupAvailableNow(restaurant) && !isDeliveryAvailableNow(restaurant);
    const shouldBeginNewOrder =
      !isClosed(restaurant) &&
      restaurant.is_live &&
      (!order || order.restaurant_id !== restaurant.id || isWrongOrderType);

    // TO DO: remove account once BE updates membership api with restaurant info
    if (
      isOnPage('membership') ||
      isOnPage('history') ||
      isOnPage('account') ||
      isOnPage('inquiry')
    ) {
      return;
    }

    // CN-10963 if there's an OrderBot temp closure don't start order
    if (
      (restaurant.closures?.length &&
        restaurant.closures.find(
          (closure) => closure.description === MAX_ORDER_CLOSURE_DESCRIPTION
        )) ||
      // Don't attempt to intiate a new order if we've somehow accessed a marketplace
      // themed menu when the resto doesn't have the option live in admin.
      !shouldBeginNewOrder ||
      // this will be undefined if the resto is closed
      !defaultFulfillMethod
    ) {
      return;
    }

    // Marketplace delivery fulfillment flow
    if (!isOnPage('checkout')) {
      setTriggerHandleDeliveryOrderFromURLParam(true);
    }

    // Begin the order
    startOrder(defaultFulfillMethod, nextAvailableTime);

    if (isDeliveryOnly) {
      setTriggerModal({
        modalType: MODAL_TYPE.fulfillmentDetails,
        modalProps: {
          fulfillmentMethod: FULFILLMENT_METHODS.delivery,
          nextWhen: nextAvailableTime,
          noCloseOverlay: true,
        },
      });
    } else if (
      nextAvailableTime &&
      isClosedNow &&
      isCateringOrderFlow() &&
      (isPickupAheadAvailable(restaurant) ||
        isDeliveryAheadAvailable(restaurant))
    ) {
      setTriggerModal({
        modalType: MODAL_TYPE.cateringFulfillment,
        modalProps: {
          fulfillmentMethod: defaultFulfillMethod,
          nextWhen: nextAvailableTime,
          noCloseOverlay: true,
        },
      });
    } else if (
      nextAvailableTime &&
      isClosedNow &&
      (isPickupAheadAvailable(restaurant) ||
        isDeliveryAheadAvailable(restaurant))
    ) {
      setTriggerModal({
        modalType: MODAL_TYPE.orderingUnavailable,
        modalProps: {
          noCloseOverlay: true,
        },
      });
    }
  }

  async function handleFetchRestaurant(
    id: string
  ): Promise<Restaurant | undefined> {
    setIsRestaurantLoading(true);

    // don't make catering restaurant API call if FF is off
    if (isOnCateringUrl && !isCateringEnabled) {
      setIsRestaurantLoading(false);
      return undefined;
    }

    const { data } = await getRestaurant(id);
    const enrichedRestaurant: Restaurant | undefined = data
      ? {
          ...data,
          country_code: getCountryCode(data),
          currency: getCurrency(data),
        }
      : undefined;

    if (enrichedRestaurant) {
      setRestaurantState(enrichedRestaurant);
      handleFetchRestaurantSuccess(enrichedRestaurant);
    }

    setIsRestaurantLoading(false);
    return enrichedRestaurant;
  }

  // Also call handle fetch restaurant if catering url changes, but the restaurant id doesn't
  useEffect(() => {
    if (!restaurantId) {
      return;
    }

    void handleFetchRestaurant(restaurantId);
  }, [restaurantId, isOnCateringUrl]);

  const values = useMemo(() => {
    if (!restaurantState) {
      return {
        ...DEFAULT_RESTAURANT_CONTEXT_VALUE,
        handleFetchRestaurant,
        isRestaurantLoading,
        setTriggerHandleDeliveryOrderFromURLParam,
        triggerHandleDeliveryOrderFromURLParam,
      };
    }

    return {
      fulfillmentEta: {
        pickup:
          restaurantState.fulfillment?.pickup?.estimated_fulfillment_time ??
          undefined,
        delivery:
          restaurantState.fulfillment?.delivery?.estimated_fulfillment_time ??
          undefined,
      },
      handleFetchRestaurant,
      isRestaurantLoading,
      isTableRequired: getIsTableRequired(restaurantState),
      getIsFulfillmentAvailableForWhen: (
        fulfillment: FulfillMethod,
        when: string
      ) => getIsFulfillmentAvailableForWhen(restaurantState, fulfillment, when),
      getIsTippingEnabled: (fulfillment: FulfillMethod) =>
        getIsTippingEnabled(
          restaurantState,
          fulfillment,
          order?.area?.allows_tip
        ),
      getNextAvailableTime: (fulfillment?: string) =>
        getNextAvailableTime(restaurantState, fulfillment),
      resetRestaurantState: () => {},
      restaurant: restaurantState,
      restaurantMembership: {
        plans: restaurantState.membership?.plans || [],
        enabled:
          !!restaurantState && getRestaurantMembershipEnabled(restaurantState),
      },
      setTriggerHandleDeliveryOrderFromURLParam,
      triggerHandleDeliveryOrderFromURLParam,
      triggerModal,
      setTriggerModal,
    };
  }, [
    order,
    restaurantState,
    isRestaurantLoading,
    triggerHandleDeliveryOrderFromURLParam,
  ]);

  return (
    <RestaurantContext.Provider value={values}>
      {children}
    </RestaurantContext.Provider>
  );
}

export { RestaurantContext, RestaurantProvider };
